import React, { useEffect, useState } from "react";
import { Luggage, Printer, Check, RotateCcw } from "lucide-react";
import { useCruise } from "../context/CruiseContext";
import { Button } from "./ui/button";

const printList = (packing, checked, title) => {
  const w = window.open("", "_blank", "width=800,height=900");
  if (!w) return;
  const groups = packing.groups
    .map(
      (g) => `<h2>${g.title}</h2><ul>${g.items
        .map((it) => `<li><span class="box">${checked[`${g.title}|${it}`] ? "&#10003;" : ""}</span>${it}</li>`)
        .join("")}</ul>`
    )
    .join("");
  w.document.write(`<html><head><title>${title}</title><style>
    body{font-family:Arial,sans-serif;color:#111;padding:24px;font-size:12px}
    h1{font-size:20px;margin:0 0 4px} p{margin:0 0 12px;color:#555}
    .cols{columns:2;column-gap:28px} h2{font-size:13px;text-transform:uppercase;letter-spacing:1px;border-bottom:2px solid #E50914;padding-bottom:3px;margin:12px 0 6px;break-after:avoid}
    ul{list-style:none;padding:0;margin:0;break-inside:avoid-column} li{display:flex;gap:8px;align-items:flex-start;margin:4px 0}
    .box{display:inline-flex;justify-content:center;align-items:center;width:12px;height:12px;border:1.5px solid #111;flex-shrink:0;font-size:10px}
  </style></head><body><h1>${title}</h1><p>${packing.subtitle}</p><div class="cols">${groups}</div></body></html>`);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 300);
};

const PackingChecklist = () => {
  const { packingList: packing, EVENT } = useCruise();
  const storeKey = `hh-packing-${EVENT.id}`;
  const [checked, setChecked] = useState({});

  useEffect(() => {
    try { setChecked(JSON.parse(localStorage.getItem(storeKey)) || {}); } catch (e) { setChecked({}); }
  }, [storeKey]);

  if (!packing) return null;

  const toggle = (key) => {
    const next = { ...checked, [key]: !checked[key] };
    setChecked(next);
    localStorage.setItem(storeKey, JSON.stringify(next));
  };
  const reset = () => { setChecked({}); localStorage.removeItem(storeKey); };

  const total = packing.groups.reduce((s, g) => s + g.items.length, 0);
  const done = Object.values(checked).filter(Boolean).length;
  const title = `${packing.title} — House Heads At Sea ${EVENT.year}`;

  return (
    <section id="packing" data-testid="packing-checklist-section" className="relative bg-[#0A0A0A] py-20 sm:py-24">
      <div className="max-w-5xl mx-auto px-5">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-10">
          <div>
            <div className="flex items-center gap-2 text-[var(--hh-gold)] mb-3">
              <Luggage size={18} />
              <p className="font-cond uppercase tracking-[0.4em] text-sm">Get Ready To Sail</p>
            </div>
            <h2 className="font-display uppercase text-white text-4xl sm:text-6xl">
              Packing <span className="text-[#E50914]">Checklist</span>
            </h2>
            <p className="mt-3 text-white/60 max-w-xl">{packing.subtitle}</p>
          </div>
          <div className="flex items-center gap-3">
            <span data-testid="packing-progress" className="font-cond uppercase tracking-widest text-xs text-[var(--hh-gold)]">
              {done}/{total} packed
            </span>
            <button onClick={reset} data-testid="packing-reset-btn" className="text-white/50 hover:text-white" aria-label="Reset checklist">
              <RotateCcw size={16} />
            </button>
            <Button
              onClick={() => printList(packing, checked, title)}
              data-testid="packing-print-btn"
              className="bg-[#E50914] hover:bg-[#c20710] text-white font-cond uppercase tracking-widest rounded-full px-6 py-5 h-auto"
            >
              <Printer size={16} className="mr-2" /> Print Checklist
            </Button>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          {packing.groups.map((g) => (
            <div key={g.title} className="bg-[#111111] border border-white/10 rounded-2xl p-6">
              <h3 className="font-cond font-bold uppercase tracking-widest text-white text-base mb-4">{g.title}</h3>
              <ul className="space-y-2.5">
                {g.items.map((it) => {
                  const key = `${g.title}|${it}`;
                  const on = !!checked[key];
                  return (
                    <li key={key}>
                      <button
                        type="button"
                        onClick={() => toggle(key)}
                        data-testid="packing-item"
                        className="w-full flex items-start gap-3 text-left group"
                      >
                        <span className={`mt-0.5 flex items-center justify-center w-5 h-5 rounded border shrink-0 transition-colors ${on ? "bg-[var(--hh-gold)] border-[var(--hh-gold)] text-black" : "border-white/30 group-hover:border-[var(--hh-gold)]"}`}>
                          {on && <Check size={14} />}
                        </span>
                        <span className={`text-sm transition-colors ${on ? "text-white/40 line-through" : "text-white/80"}`}>{it}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PackingChecklist;
